import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import "./ProductDetail.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  ImageSelector,
  ProductDescription,
  ProductVariationSelector,
  QuantitySelector,
  Button,
  ButtonType,
  ErrorBox,
  Alert,
} from "@amzn/burberry-ui-components";
import { awsRum } from "src/rum";
import { AppDispatch } from "src/store/store";
import { getStage } from "src/utils/roadieHelper";
import {
  hydrateCatalog,
  selectCatalog,
  selectHydrateCatalogComplete,
  selectCatalogError,
  selectFailed,
} from "src/store/catalogSlice";
import { addToCart, selectCart } from "src/store/cartSlice";
import { selectCsrfToken, getValidateToken, selectValidateTokenComplete } from "src/store/authSlice";
import { selectTemplate, selectUseTransparentImages } from "src/store/templateSlice";
import { metricEmitter } from "src/utils/metricEmitter";
import {
  getPriceNumberFromString,
  isCatalogItemUnavailable,
  formatAvailabilityString,
  parseDeliveryMessage,
  buildCombinedShippingAndImportCostString,
  hydrateCatalogFromCart,
  sanitizeAsin,
  getValidCart,
  identifierExistsInCart,
  findVariation,
  findDimensionValueUnavailabilities,
  MAX_ASINS_IN_CART,
  PDP_ITEM_NOT_AVAILABLE,
  MAX_ITEMS_REACHED_TEXT,
  UH_OH,
  GENERIC_ERROR_STRING,
  ADD_TO_CART_SUCCESS,
  ADD_TO_CART_MAX,
  AlertType
} from "@amzn/d2c-application-website-common";
import { resolveAllImageUrls } from "src/utils/imageUtil";

const LOADING_TEXT = "Loading...";

const ProductDetail: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { asin: rawAsin } = useParams<{ asin: string }>();
  const [searchParams] = useSearchParams();
  const asin = sanitizeAsin(rawAsin || '');
  const urlCustomId = searchParams.get('customId') || undefined;
  const urlCustomizationToken = searchParams.get('customizationToken') || undefined;
  const cart = useSelector(selectCart);
  const catalog = useSelector(selectCatalog);
  const hydrateCatalogComplete = useSelector(selectHydrateCatalogComplete);
  const catalogError = useSelector(selectCatalogError);
  const failedCatalog = useSelector(selectFailed);
  const csrfToken = useSelector(selectCsrfToken);
  const accessTokenExists = useSelector(selectValidateTokenComplete);
  const template = useSelector(selectTemplate);
  const useTransparentImages = useSelector(selectUseTransparentImages);

  const [quantity, setQuantity] = useState(1);
  const [selectedVariationIndexes, setSelectedVariationIndexes] = useState<
    number[]
  >([]);
  const [selectedVariation, setSelectedVariation] = useState<{
    asin?: string; customId?: string; customizationToken?: string;
  } | null>(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [pageLoadTime, setPageLoadTime] = useState(Date.now());
  const [canAddToCart, setCanAddToCart] = useState(true);
  const [validHydratedCartItems, setValidHydratedCartItems] = useState<any[]>([]);
  const [invalidCatalogItem, setInvalidCatalogItem] = useState(false);
  const [dimensionValueUnavailabilities, setDimensionValueUnavailabilities] = useState<boolean[][]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<AlertType>(AlertType.SUCCESS);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTrigger, setAlertTrigger] = useState(0);

  // Local identifier state drives catalog hydration.
  // When variation changes, we update identifier → triggers re-hydration → catalog updates → catalogItem updates.
  const [identifier, setIdentifier] = useState<{ itemAsin: string; customId?: string; customizationToken?: string }>({
    itemAsin: asin || '',
    customId: urlCustomId,
    customizationToken: urlCustomizationToken,
  });

  // Local catalogItem state — only updated when a new match is found, never cleared.
  // This prevents page flicker during variation re-fetch.
  const [catalogItem, setCatalogItem] = useState<any>(null);

  const minOrderQuantity = catalogItem?.availability?.minOrderQuantity ?? 1;
  const maxOrderQuantity = catalogItem?.availability?.maxOrderQuantity ?? 99;

  // Reset state when navigating to a different product
  useEffect(() => {
    setSelectedImage("");
    setQuantity(1);
    setPageLoadTime(Date.now());
    setInvalidCatalogItem(false);
    setSelectedVariation(null);
    setCatalogItem(null);
    setCanAddToCart(true);
    setIdentifier({ itemAsin: asin || '', customId: urlCustomId, customizationToken: urlCustomizationToken });
  }, [asin]);

  // Enforce min order quantity when catalog loads
  useEffect(() => {
    setQuantity(prev => Math.max(prev, minOrderQuantity));
  }, [minOrderQuantity]);

  // updateVariation helper — updates dimension indexes, unavailabilities, URL, and optionally identifier
  const updateVariationFromCatalog = (
    productVariations: any,
    variation: any,
    newIdentifier: boolean,
  ) => {
    if (!variation || !variation.dimensionLocation) {
      return;
    }
    setSelectedVariationIndexes(variation.dimensionLocation);
    setDimensionValueUnavailabilities(
      findDimensionValueUnavailabilities(
        productVariations?.variations ?? [],
        productVariations?.dimensions ?? [],
        variation.dimensionLocation ?? [],
      ),
    );
    setSelectedVariation({
      asin: variation.asin,
      customId: variation.customId,
      customizationToken: variation.customizationToken,
    });

    // Update browser URL for sharing/refresh (replaceState keeps React Router's route unchanged,
    // which is intentional — the component uses local identifier state, not useParams)
    const currentUrl = new URL(window.location.href);
    const pathMatch = currentUrl.pathname.match(/\/product\/([A-Za-z0-9]+)/);
    if (pathMatch && variation.asin) {
      currentUrl.pathname = currentUrl.pathname.replace(pathMatch[0], `/product/${variation.asin}`);
    }
    if (variation.customId) {
      currentUrl.searchParams.set('customId', variation.customId);
    } else {
      currentUrl.searchParams.delete('customId');
    }
    if (variation.customizationToken) {
      currentUrl.searchParams.set('customizationToken', variation.customizationToken);
    } else {
      currentUrl.searchParams.delete('customizationToken');
    }
    window.history.replaceState(null, '', currentUrl.toString());

    // Update identifier if needed — triggers re-hydration
    if (newIdentifier) {
      const nextIdentifier = {
        itemAsin: variation.asin || identifier.itemAsin || '',
        customId: variation.customId,
        customizationToken: variation.customizationToken,
      };
      const identifierChanged =
        nextIdentifier.itemAsin !== identifier.itemAsin ||
        nextIdentifier.customId !== identifier.customId ||
        nextIdentifier.customizationToken !== identifier.customizationToken;
      if (identifierChanged) {
        setIdentifier(nextIdentifier);
      }
    }
  };

  useEffect(() => {
    if (csrfToken) {
      dispatch(getValidateToken({ stage: getStage(), csrfToken }));
    }
  }, []);

  // Page view metric
  useEffect(() => {
    awsRum?.recordEvent("PageView", {
      page: "ProductDetail",
      asin: identifier.itemAsin || "",
    });
  }, [identifier.itemAsin]);

  // Page load metric
  useEffect(() => {
    if (hydrateCatalogComplete && catalogItem) {
      const loadTime = Date.now() - pageLoadTime;
      awsRum?.recordEvent("PageLoad", {
        page: "ProductDetail",
        loadTimeMs: String(loadTime),
      });
    }
  }, [hydrateCatalogComplete, catalogItem]);

  // Update document title
  useEffect(() => {
    if (catalogItem) {
      document.title = template?.title
        ? `${catalogItem.name} - ${template.title}`
        : catalogItem.name || '';
    }
  }, [catalogItem, template]);

  // Hydrate catalog whenever identifier changes
  useEffect(() => {
    if (identifier.itemAsin) {
      dispatch(
        hydrateCatalog({
          itemIdentifiers: [identifier],
          stage: getStage(),
          locale: "en-US",
          csrfToken,
        }),
      );
    }
  }, [dispatch, identifier, accessTokenExists, csrfToken]);

  // When catalog updates, find the matching item and update local state.
  // Only update (never clear) to prevent flicker during re-fetch.
  useEffect(() => {
    if (catalog && catalog.length > 0) {
      const merchItem = catalog.find(
        (item: any) =>
          item.identifier?.itemAsin === identifier.itemAsin &&
          (identifier.customId === undefined ||
            item.identifier?.customId === identifier.customId) &&
          (identifier.customizationToken === undefined ||
            item.identifier?.customizationToken === identifier.customizationToken),
      );

      // undefined check prevents page flickering as merchItem can be undefined while waiting for catalog updates
      if (merchItem !== undefined) {
        const isNewItem =
          merchItem.identifier?.itemAsin !== catalogItem?.identifier?.itemAsin ||
          merchItem.identifier?.customId !== catalogItem?.identifier?.customId ||
          merchItem.identifier?.customizationToken !== catalogItem?.identifier?.customizationToken;

        setCatalogItem(merchItem);
        // Only reset selected image when the item actually changes (new variation loaded)
        if (isNewItem && merchItem.images && merchItem.images.length > 0) {
          const newImages = resolveAllImageUrls(merchItem.images, useTransparentImages);
          if (newImages.length > 0) {
            setSelectedImage(newImages[0].src);
          }
        }
      }

      // Find selected variation within the catalog item and update
      const variations = merchItem?.variations;
      // An ASIN-only request returns the parent metadata and can contain a
      // variation-specific image set only after the selected variation is
      // included in the next request.
      const hasVariationIdentifier =
        identifier.customId !== undefined || identifier.customizationToken !== undefined;

      const isParentAsinOnlyRequest =
        !hasVariationIdentifier &&
        !!variations?.variations?.length &&
        variations.variations.every(
          (variation: any) => variation.asin === identifier.itemAsin,
        );
      const selectedVar = isParentAsinOnlyRequest
        ? undefined
        : variations?.variations?.find(
            (variation: any) =>
              variation.asin === identifier.itemAsin &&
              (identifier.customId === undefined || variation.customId === identifier.customId) &&
              (identifier.customizationToken === undefined ||
                variation.customizationToken === identifier.customizationToken),
          );

      if (selectedVar) {
        updateVariationFromCatalog(variations, selectedVar, false);
      } else if (variations?.variations?.[0]) {
        updateVariationFromCatalog(variations, variations.variations[0], true);
      }
    }
  }, [catalog, identifier]);

  // Hydrate catalog for all cart items on page load so we can accurately check max items
  const cartItemKeys = cart?.items?.map((i: any) =>
    [i.asin, i.customId, i.customizationToken].filter(Boolean).join(':')
  ).join(',') ?? '';

  useEffect(() => {
    if (cart?.items?.length && csrfToken) {
      hydrateCatalogFromCart(cart, dispatch, hydrateCatalog, getStage(), csrfToken);
    }
  }, [csrfToken, cartItemKeys]);

  // Get valid hydrated cart items to check against max unique items
  useEffect(() => {
    if (catalog && cart) {
      const validCart = getValidCart(cart.items || [], catalog);
      setValidHydratedCartItems(validCart.cartItems);
    }
  }, [catalog, cart]);

  // Enforce max unique items in cart
  useEffect(() => {
    if (catalogItem?.identifier && validHydratedCartItems) {
      const maxItemsReached = validHydratedCartItems.length >= MAX_ASINS_IN_CART;
      const itemAlreadyInCart = identifierExistsInCart(validHydratedCartItems, catalogItem.identifier);
      setCanAddToCart(!maxItemsReached || (maxItemsReached && itemAlreadyInCart));
    }
  }, [validHydratedCartItems, catalogItem]);

  // Mark item as invalid if it failed catalog hydration
  useEffect(() => {
    if (failedCatalog && identifier.itemAsin) {
      if (failedCatalog.some((item: any) => item.itemAsin === identifier.itemAsin)) {
        setInvalidCatalogItem(true);
      }
    }
  }, [failedCatalog, identifier.itemAsin]);

  // Use the variation-specific catalog item for images when available.
  const resolvedImages = resolveAllImageUrls(catalogItem?.images, useTransparentImages);

  const handleAddToCart = () => {
    if (!catalogItem) return;
    const itemAsin = selectedVariation?.asin || catalogItem.identifier?.itemAsin || "";
    const customId = selectedVariation?.customId || catalogItem.identifier?.customId;
    const customizationToken = selectedVariation?.customizationToken || catalogItem.identifier?.customizationToken;
    awsRum?.recordEvent("ButtonClick", {
      action: "AddToCart",
      asin: itemAsin,
      quantity: String(quantity),
    });
    const existingCartItem = cart?.items?.find((i: any) =>
      i.asin === itemAsin &&
      (customId === undefined || i.customId === customId) &&
      (customizationToken === undefined || i.customizationToken === customizationToken),
    );
    const existingQuantity = existingCartItem?.quantity ?? 0;

    if (existingQuantity + quantity > maxOrderQuantity) {
      setAlertType(AlertType.ERROR);
      setAlertMessage(ADD_TO_CART_MAX);
      setAlertTrigger(prev => prev + 1);
      setShowAlert(true);
      return;
    }

    dispatch(
      addToCart({
        purchaseItem: {
          quantity,
          asin: itemAsin,
          customId,
          customizationToken,
          offerId: existingCartItem?.offerId || catalogItem.offerId || "",
          merchantId: existingCartItem?.merchantId || catalogItem.merchantId || "",
        },
        quantity,
        subtotal:
          quantity *
          getPriceNumberFromString(catalogItem.price || "0"),
      }),
    );
    setAlertType(AlertType.SUCCESS);
    setAlertMessage(ADD_TO_CART_SUCCESS);
    setAlertTrigger(prev => prev + 1);
    setShowAlert(true);
  };

  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => setShowAlert(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [alertTrigger, showAlert]);

  const handleVariationChange = (
    dimensionIndex: number,
    valueIndex: number,
  ) => {
    awsRum?.recordEvent("VariationSelect", {
      asin: identifier.itemAsin || "",
      dimensionIndex: String(dimensionIndex),
      valueIndex: String(valueIndex),
    });

    // Use findVariation then updateVariationFromCatalog
    const variations = catalogItem?.variations?.variations || [];
    const matchedVariation = findVariation(variations, dimensionIndex, valueIndex, selectedVariationIndexes);

    if (matchedVariation) {
      updateVariationFromCatalog(catalogItem?.variations, matchedVariation, true);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    awsRum?.recordEvent("QuantityChange", {
      asin: identifier.itemAsin || "",
      quantity: String(newQuantity),
    });
    setQuantity(newQuantity);
  };

  const handleImageSelect = (image: string) => {
    awsRum?.recordEvent("ImageSelect", { asin: identifier.itemAsin || "", image });
    setSelectedImage(image);
  };

  // Invalid ASIN — hard error, nothing to render
  if (!asin) {
    return (
      <div
        className="productDetailPage productDetailPage--error"
        data-testid="productDetailPage"
      >
        <ErrorBox
          heading={UH_OH}
          message={GENERIC_ERROR_STRING}
          metricEmitter={metricEmitter}
        />
      </div>
    );
  }

  const showError = invalidCatalogItem || catalogError || (hydrateCatalogComplete && !catalogItem);

  return (
    <div className="productDetailPage" data-testid="productDetailPage">
      {showError && (
        <div className="productDetailPage__error" data-testid="productDetailError">
          <ErrorBox
            heading={UH_OH}
            message={GENERIC_ERROR_STRING}
            metricEmitter={metricEmitter}
          />
        </div>
      )}
      {!showError && (
        <>
          {catalogItem?.images && (
            <div className="productDetailPage__images">
              <ImageSelector
                images={resolvedImages}
                selectedImage={selectedImage || resolvedImages[0]?.src || ""}
                productName={catalogItem.name || ""}
                onImageSelect={handleImageSelect}
                mainImageWidth={template?.pdpMainImageWidth}
                metricEmitter={metricEmitter}
              />
            </div>
          )}
          {catalogItem?.name && (
            <div className="productDetailPage__info">
              <ProductDescription
                title={catalogItem.name || ""}
                price={catalogItem.price || ""}
                details={catalogItem.descriptionBullets}
                availability={formatAvailabilityString(catalogItem.availability?.primaryMessage, catalogItem.availability?.secondaryMessage)}
                isPreOrder={catalogItem.availability?.type === 'PREORDER'}
                deliveryMessage={parseDeliveryMessage(catalogItem?.deliveryMessage)}
                importFee={catalogItem?.importDetails?.fee}
                importMessage={catalogItem?.importDetails?.message}
                combinedFeeMessage={(() => {
                  const fee = catalogItem?.importDetails?.fee;
                  const msg = catalogItem?.importDetails?.message;
                  const delivery = parseDeliveryMessage(catalogItem?.deliveryMessage);
                  const feeNum = Number(fee?.replace('$', '') ?? '0') || 0;
                  if (fee && feeNum !== 0 && msg && delivery) {
                    return buildCombinedShippingAndImportCostString(fee, delivery, msg);
                  }
                  return undefined;
                })()}
                returnsText={!isCatalogItemUnavailable(catalogItem.availability) ? catalogItem?.returnsPolicy?.policyMessage : undefined}
                primeBadgeData={catalogItem?.primeBadgeData}
              />

              {catalogItem.variations?.dimensions &&
                catalogItem.variations.dimensions.length > 0 && (
                  <ProductVariationSelector
                    dimensions={catalogItem.variations.dimensions.map(
                      (dim: any) => ({
                        name: dim.name || "",
                        values: dim.values || [],
                      }),
                    )}
                    selectedValues={selectedVariationIndexes}
                    availableOptions={dimensionValueUnavailabilities.length > 0
                      ? dimensionValueUnavailabilities.map((dim) => dim.map((unavailable) => !unavailable))
                      : undefined}
                    onVariationChange={handleVariationChange}
                  />
                )}

              {!isCatalogItemUnavailable(catalogItem.availability) && (
                <div className="productDetailPage__actions">
                  <QuantitySelector
                      quantity={quantity}
                      minQuantity={minOrderQuantity}
                      maxQuantity={maxOrderQuantity}
                      setQuantity={handleQuantityChange}
                      metricEmitter={metricEmitter}
                  />
                </div>
              )}

              <Button
                type={ButtonType.ADD_TO_CART}
                text={
                  !hydrateCatalogComplete
                    ? LOADING_TEXT
                    : isCatalogItemUnavailable(catalogItem.availability)
                      ? PDP_ITEM_NOT_AVAILABLE
                      : !canAddToCart
                        ? MAX_ITEMS_REACHED_TEXT
                        : undefined
                }
                onClick={handleAddToCart}
                metricEmitter={metricEmitter}
                disabled={
                  isCatalogItemUnavailable(catalogItem.availability) ||
                  !canAddToCart ||
                  !hydrateCatalogComplete
                }
              />
            </div>
          )}
        </>
      )}
      {showAlert && (
        <Alert
          message={alertMessage}
          severity={alertType === AlertType.SUCCESS ? 'success' : 'error'}
          action={alertType === AlertType.SUCCESS ? <Link to="/cart">View Cart</Link> : undefined}
        />
      )}
    </div>
  );
};

export default ProductDetail;
